import { Link } from 'react-router-dom';
import { LandingPageContent } from './landing-page-content';
import { Navbar } from '@common/ui/navigation/navbar/navbar';
import { MixedImage } from '@common/ui/images/mixed-image';
import { Footer } from '@common/ui/footer/footer';
import { Trans } from '@common/i18n/trans';
import { AdHost } from '@common/admin/ads/ad-host';
import { Fragment, useEffect, useRef, useState } from 'react';
import { DefaultMetaTags } from '@common/seo/default-meta-tags';
import { useSettings } from '@common/core/settings/use-settings';
import { LandingPageNewLinkForm } from '@app/landing/landing-page-new-link-form';
import { useAuth } from '@common/auth/use-auth';
import { LandingPageStats } from '@app/landing/landing-page-stats';
import { LaunchOfferBanner } from '@app/landing/launch-offer-banner';
import { PricingTable } from '@common/billing/pricing-table/pricing-table';
import './landing-page.css';


interface ContentProps {
  content: LandingPageContent;
}

export function LandingPage() {
  const settings = useSettings();
  const homepage = settings.homepage as { appearance: LandingPageContent };
  const showPricing = settings.links.homepage_pricing && settings.billing.enable;

  return (
    <Fragment>
      <DefaultMetaTags />
      <Fragment>
        <Navbar color="bg" border="border-b" className="lp-sticky-nav" menuPosition="homepage-navbar" />
        <HeroHeader content={homepage.appearance} />
        <AdHost slot="landing" className="mx-14 mb-14 md:mb-70" />
        <TrustedBy />
        <PrimaryFeatures content={homepage.appearance} />
        <CreatorsSection />
        <PhoneCarousel />
        {settings.links?.homepage_stats && <LandingPageStats />}
        {showPricing && <PricingSection content={homepage.appearance} />}
        <SecondaryFeatures content={homepage.appearance} />
        <HowItWorks />
        <FaqSection />
        <GetStartedCta content={homepage.appearance} />
        <InternalLinksSection />
        <Footer />
      </Fragment>
    </Fragment>
  );
}

/* ── HERO ── */
function HeroHeader({ content }: ContentProps) {
  const { hasPermission } = useAuth();
  const { links: { homepage_creation } } = useSettings();

  const AVATAR_COLORS = ['#e07b39', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'];
  const AVATAR_INITIALS = ['A', 'K', 'M', 'S', 'J'];

  return (
    <header className="lp-hero">
      <div className="lp-hero-body">
        {/* Left column */}
        <div className="lp-hero-text">
          <LaunchOfferBanner className="mb-16 inline-block" />
          <span className="section-label">THE LINK IN BIO, DONE RIGHT</span>
          <h1 className="lp-hero-h1">
            <Trans message={content.headerTitle} />
          </h1>
          <p className="lp-hero-sub">
            <Trans message={content.headerSubtitle} />
          </p>

          {homepage_creation && hasPermission('links.create') && (
            <LandingPageNewLinkForm />
          )}

          <div className="lp-social-proof">
            <div className="lp-avatars">
              {AVATAR_INITIALS.map((initial, i) => (
                <span key={i} className="lp-avatar" style={{ background: AVATAR_COLORS[i], marginLeft: i === 0 ? 0 : -10, zIndex: 5 - i }}>
                  {initial}
                </span>
              ))}
            </div>
            <span className="lp-proof-text">
              <strong>120,000+</strong> creators share their world with LinkinBio
            </span>
          </div>
        </div>

        {/* Right column - phone mockup */}
        <div className="lp-hero-phone-wrap">
          <div className="lp-hero-phone-glow" />
          {content.headerImage ? (
            <img
              src={content.headerImage}
              alt="Bio page preview"
              className="lp-hero-phone-img"
              decoding="async"
              {...({fetchpriority: 'high'} as any)}
            />
          ) : (
            <img
              src="/images/landing/hero-phone.webp"
              alt="Bio page preview"
              className="lp-hero-phone-img"
              decoding="async"
              {...({fetchpriority: 'high'} as any)}
            />
          )}
        </div>
      </div>
    </header>
  );
}

/* ── TRUSTED BY ── */
const PLATFORMS = ['Instagram', 'TikTok', 'YouTube', 'Twitch', 'Substack'];

function TrustedBy() {
  return (
    <div className="lp-trusted">
      <span className="lp-trusted-label">TRUSTED BY CREATORS ON</span>
      <div className="lp-trusted-logos">
        {PLATFORMS.map(p => (
          <span key={p} className="lp-trusted-name">{p}</span>
        ))}
      </div>
    </div>
  );
}

/* ── PRIMARY FEATURES — 2×2 GRID ── */

function UrlShortenerMock() {
  return (
    <div className="lp-url-mock">
      <div className="lp-url-row">
        <span className="lp-url-input">youtube.com/watch?v=8Hj2...&amp;list=PLx</span>
        <button className="lp-url-btn">Shorten</button>
      </div>
      <div className="lp-url-result">
        <span className="lp-url-short-icon">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ff4f00" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        </span>
        <span className="lp-url-short-text">lnkb.io/edit-5min</span>
        <span className="lp-url-copy-btn">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
        </span>
      </div>
    </div>
  );
}

/* icon svgs — white stroke, used inside the orange app-icon box */
const BioSvg = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" />
    <line x1="9" y1="7" x2="15" y2="7" />
    <line x1="9" y1="11" x2="15" y2="11" />
    <line x1="9" y1="15" x2="13" y2="15" />
  </svg>
);
const LinkSvg = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);
const PersonSvg = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const ChartSvg = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

function AppIcon({ svg, light }: { svg: React.ReactNode; light?: boolean }) {
  return (
    <div className="lp-feat-appicon" style={light ? { background: 'rgba(255,255,255,0.22)' } : undefined}>
      {svg}
    </div>
  );
}

function BarChart() {
  const bars = [
    { h: 38 }, { h: 52 }, { h: 44 }, { h: 68 }, { h: 40 }, { h: 36 }, { h: 72 }, { h: 48 },
  ];
  return (
    <div className="lp-feat-barchart">
      {bars.map((b, i) => (
        <div key={i} className="lp-feat-bar" style={{ height: b.h, opacity: i % 2 === 1 ? 1 : 0.45 }} />
      ))}
    </div>
  );
}

function PrimaryFeatures({ content }: ContentProps) {
  const features = content.primaryFeatures || [];
  const f0 = features[0];
  const f1 = features[1];
  const f2 = features[2];
  const f3 = features[3];

  return (
    <section className="lp-features-section" id="features">
      <div className="landing-container">
        <div className="section-header">
          <small className="section-label">EVERYTHING IN ONE PLACE</small>
          <h2 className="lf-heading">Your whole internet,<br />one tidy link.</h2>
          <p className="section-sub">
            Stop cramming links into your bio. Give your audience one home for all of it.
          </p>
        </div>

        <div className="lp-feat-grid">
          {/* Card 0 — white */}
          <div className="lp-feat-card lp-feat-white" data-testid="primary-root-0">
            <AppIcon svg={<BioSvg />} />
            <h3 className="lp-feat-title">{f0?.title || 'A bio page that feels like you'}</h3>
            <p className="lp-feat-sub lp-feat-sub-gray">{f0?.subtitle || 'Drag, drop and theme your page in minutes. Add buttons, embeds, products and socials.'}</p>
            <div className="lp-feat-arrow">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
              </svg>
            </div>
          </div>

          {/* Card 1 — dark */}
          <div className="lp-feat-card lp-feat-dark" data-testid="primary-root-1">
            <AppIcon svg={<LinkSvg />} />
            <h3 className="lp-feat-title" style={{ color: '#fff' }}>
              {f1?.title || 'Shorten any URL instantly'}
            </h3>
            <p className="lp-feat-sub" style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 20 }}>
              {f1?.subtitle || 'Turn long, ugly links into clean branded short links you can drop anywhere.'}
            </p>
            <UrlShortenerMock />
          </div>

          {/* Card 2 — white */}
          <div className="lp-feat-card lp-feat-white" data-testid="primary-root-2">
            <AppIcon svg={<PersonSvg />} />
            <h3 className="lp-feat-title">{f2?.title || 'Custom handle'}</h3>
            <p className="lp-feat-sub lp-feat-sub-gray">{f2?.subtitle || 'Claim your name and own your link forever.'}</p>
          </div>

          {/* Card 3 — orange gradient */}
          <div className="lp-feat-card lp-feat-orange" data-testid="primary-root-3">
            <AppIcon svg={<ChartSvg />} light />
            <h3 className="lp-feat-title" style={{ color: '#fff' }}>
              {f3?.title || 'Know exactly what works'}
            </h3>
            <p className="lp-feat-sub" style={{ color: 'rgba(255,255,255,0.8)', marginBottom: 20 }}>
              {f3?.subtitle || 'Real-time clicks, views and top links — so you can double-down on what your audience loves.'}
            </p>
            <BarChart />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── HOW IT WORKS ── */
const HIW_STEPS = [
  {
    num: '1',
    title: 'Claim your name',
    desc: 'Pick your handle and grab linkinbio.site/you – free, forever.',
  },
  {
    num: '2',
    title: 'Add your links',
    desc: 'Drop in videos, shops, socials and more. Theme it to match your vibe.',
  },
  {
    num: '3',
    title: 'Share & track',
    desc: 'Put one link in your bio and watch the clicks roll in from your dashboard.',
  },
];

function HowItWorks() {
  return (
    <section id="how-it-works" className="lp-hiw-section">
      <div className="landing-container">
        <div className="section-header">
          <small className="section-label">LIVE IN 3 MINUTES</small>
          <h2 className="lf-heading">From zero to your link, fast.</h2>
        </div>
        <div className="lp-hiw-grid">
          {HIW_STEPS.map((step, i) => (
            <div key={i} className="lp-hiw-step">
              <div className="lp-hiw-num">{step.num}</div>
              <h3 className="lp-hiw-title">{step.title}</h3>
              <p className="lp-hiw-desc">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── PRICING ── */
function PricingSection({ content }: ContentProps) {
  return (
    <div className="lp-pricing-section" id="pricing">
      <div className="landing-container">
        <div className="section-header">
          <small className="section-label">SIMPLE PRICING</small>
          <h2 className="lf-heading" data-testid="pricingTitle">
            <Trans message={content.pricingTitle} />
          </h2>
          <p className="section-sub" data-testid="pricingSubtitle">
            <Trans message={content.pricingSubtitle} />
          </p>
        </div>
        <PricingTable selectedCycle="monthly" productLoader="landingPage" />
      </div>
    </div>
  );
}

/* ── CREATORS / TESTIMONIAL ── */
const CREATOR_DATA = [
  { initials: 'AK', name: 'Attachment Key', verified: false, followers: '84K', bg: 'linear-gradient(145deg,#e8d5c8,#c9a990)', avatarBg: 'rgba(100,60,30,0.45)' },
  { initials: 'CM', name: 'Charly Mata', verified: true, followers: '1M', bg: 'linear-gradient(145deg,#1a2e4a,#2d5a8e)', avatarBg: 'rgba(160,82,45,0.5)' },
  { initials: 'FG', name: 'Flavia Gamonar', verified: true, followers: '1M', bg: 'linear-gradient(145deg,#c8a882,#7a5030)', avatarBg: 'rgba(60,30,15,0.4)' },
  { initials: 'AA', name: 'Austin Archer', verified: true, followers: '130K', bg: 'linear-gradient(145deg,#e8c49a,#a06828)', avatarBg: 'rgba(80,40,10,0.4)' },
  { initials: null, name: 'AC Milan', verified: true, followers: '17.6M', bg: '#0d0d0d', avatarBg: null },
  { initials: 'JF', name: 'Jo Franco', verified: true, followers: '229K', bg: 'linear-gradient(145deg,#e8a030,#8a4808)', avatarBg: 'rgba(60,20,0,0.4)' },
  { initials: 'ED', name: 'Erick Donoghue', verified: true, followers: '1.1K', bg: 'linear-gradient(145deg,#3a3a3a,#0e0e0e)', avatarBg: 'rgba(255,255,255,0.12)' },
  { initials: 'AS', name: 'Air Square', verified: true, followers: '14.2K', bg: 'linear-gradient(145deg,#f5c030,#e07800)', avatarBg: 'rgba(100,50,0,0.35)' },
  { initials: 'MR', name: 'Maya Rose', verified: true, followers: '3.4M', bg: 'linear-gradient(145deg,#c084fc,#7c3aed)', avatarBg: 'rgba(60,0,120,0.35)' },
  { initials: 'TK', name: 'Tyler Knox', verified: true, followers: '780K', bg: 'linear-gradient(145deg,#34d399,#065f46)', avatarBg: 'rgba(0,60,30,0.35)' },
];

function CreatorsSection() {
  const igIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <path d="M8.56 2.75c4.37 6.03 6.02 9.42 8.03 17.72m2.54-15.38c-3.72 4.35-8.94 5.46-12.48 6.32" />
    </svg>
  );
  const checkMark = (
    <svg viewBox="0 0 10 10" fill="none">
      <polyline points="2,5 4.5,7.5 8,3" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const allCards = [...CREATOR_DATA, ...CREATOR_DATA];
  return (
    <section id="creators-embed">
      <div className="section-header">
        <small className="section-label">Testimonial</small>
        <h2 className="section-title">Social Proof / Creators Section</h2>
        <p className="section-sub">
          Join thousands of creators, marketers, and small businesses who manage their links with LinkInBio every day.
        </p>
      </div>
      <div className="marquee-outer">
        <div className="marquee-track">
          {allCards.map((c, i) => (
            <div key={i} className="creator-card">
              <div className="card-bg" style={{ background: c.bg }}>
                {c.initials === null ? (
                  <div style={{ width: 78, height: 78, borderRadius: '50%', border: '2.5px solid #cc0000', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: -50, background: '#1a0000' }}>
                    <svg width="48" height="48" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="47" fill="#cc0000" />
                      <rect x="28" y="32" width="13" height="36" fill="#111" />
                      <rect x="43" y="32" width="13" height="36" fill="#fff" />
                      <rect x="58" y="32" width="13" height="36" fill="#111" />
                      <text x="50" y="24" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="700" fontFamily="sans-serif">ACM</text>
                      <text x="50" y="84" textAnchor="middle" fill="#fff" fontSize="9" fontFamily="sans-serif">1899</text>
                    </svg>
                  </div>
                ) : (
                  <div className="avatar-initials" style={{ background: c.avatarBg! }}>{c.initials}</div>
                )}
              </div>
              <div className="card-shine" />
              <div className="card-overlay">
                <div className="creator-name">
                  {c.name}
                  {c.verified && <span className="verified-icon">{checkMark}</span>}
                </div>
                <div className="follower-count">{igIcon}{c.followers} followers</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── PHONE CAROUSEL ── */
function PhoneMock({ img, center }: { img: string; center?: boolean }) {
  return (
    <img
      src={img}
      alt=""
      className={center ? 'lf-phone-center' : 'lf-phone-side'}
      style={{
        height: center ? 'min(650px, 68vh)' : 'min(530px, 56vh)',
        width: 'auto',
        flexShrink: 0,
        display: 'block',
        transform: center ? 'translateY(-3vh)' : undefined,
        position: center ? 'relative' : undefined,
        zIndex: center ? 2 : undefined,
        filter: center
          ? 'drop-shadow(0 20px 40px rgba(0,0,0,0.4))'
          : 'drop-shadow(0 15px 28px rgba(0,0,0,0.28))',
      }}
    />
  );
}

const CAROUSEL_SLIDES = [
  { imgs: ['/phone-screens/basic.DuPDlgvB.webp', '/phone-screens/summer._wbdH2hK.webp', '/phone-screens/pride.BpG7t3VB.webp'] },
  { imgs: ['/phone-screens/chameleon.C-GjyGpf.webp', '/phone-screens/retro.tmNX0151.webp', '/phone-screens/strawberry.BJSzuun2.webp'] },
  { imgs: ['/phone-screens/desert.DXvdLGal.webp', '/phone-screens/rainy-night.fNTxc-2o.webp', '/phone-screens/xmas.C56RcPlE.webp'] },
];

function PhoneCarousel() {
  const [slide, setSlide] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const n = CAROUSEL_SLIDES.length;

  useEffect(() => {
    const update = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const scrolled = Math.max(0, -rect.top);
      const totalScroll = sectionRef.current.offsetHeight - vh;
      const progress = totalScroll > 0 ? Math.min(1, scrolled / totalScroll) : 0;
      setSlide(Math.min(n - 1, Math.floor(progress * n)));
    };
    window.addEventListener('scroll', update, { passive: true });
    update();
    return () => window.removeEventListener('scroll', update);
  }, [n]);

  const current = CAROUSEL_SLIDES[slide];

  return (
    <div id="lf-carousel-section" ref={sectionRef}>
      <div id="lf-carousel-sticky">
        <div className="lf-section-top">
          <small className="section-label">Customise your page</small>
          <h2 className="lf-heading" style={{ marginBottom: '52px' }}>
            Make it <span>yours</span>
          </h2>
        </div>
        <div className="lf-slides">
          <div className="lf-slide lf-active">
            <div className="lf-phone-wrap">
              <PhoneMock img={current.imgs[0]} />
              <PhoneMock img={current.imgs[1]} center />
              <PhoneMock img={current.imgs[2]} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── SECONDARY FEATURES — CARD GRID ── */

/* icon gradients all use the CTA palette: #1a0800 dark base + #FF4F00 / #FFB23E / #FF2E63 */
const SF_ICON_GRADS: [string, string][] = [
  ['#FF4F00', '#FFB23E'],
  ['#1a0800', '#FF4F00'],
  ['#FF4F00', '#FF2E63'],
  ['#1a0800', '#FFB23E'],
  ['#FF2E63', '#FF4F00'],
  ['#FF4F00', '#FFB23E'],
  ['#1a0800', '#FF2E63'],
  ['#FFB23E', '#FF4F00'],
  ['#FF4F00', '#1a0800'],
  ['#FF2E63', '#FFB23E'],
];

const SF_ICON_SVGS: React.ReactNode[] = [
  <svg key="link" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>,
  <svg key="bar" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
  </svg>,
  <svg key="grid" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>,
  <svg key="globe" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>,
  <svg key="users" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>,
  <svg key="zap" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>,
  <svg key="heart" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>,
  <svg key="star" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>,
  <svg key="shield" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>,
  <svg key="monitor" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
  </svg>,
];

function SecondaryFeatures({ content }: ContentProps) {
  const features = content.secondaryFeatures;

  return (
    <section id="features-stack" className="sf-section">
      <div className="landing-container">
        <div className="section-header">
          <small className="section-label">CORE FEATURES</small>
          <h2 className="lf-heading">Everything you need,<br />nothing you don't.</h2>
          <p className="section-sub">Powerful tools built for creators, brands, and businesses ready to grow.</p>
        </div>
        <div className="sf-grid">
          {features.map((feature, index) => {
            const grad = SF_ICON_GRADS[index % SF_ICON_GRADS.length];
            const iconSvg = SF_ICON_SVGS[index % SF_ICON_SVGS.length];
            return (
              <div
                key={index}
                className="sf-card"
                data-testid={`secondary-root-${index}`}
              >
                <div
                  className="sf-card-icon"
                  style={{ background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})` }}
                >
                  {iconSvg}
                </div>
                <h3 className="sf-card-title" data-testid={`secondary-title-${index}`}>
                  <Trans message={feature.title} />
                </h3>
                <p className="sf-card-desc" data-testid={`secondary-description-${index}`}>
                  <Trans message={feature.description} />
                </p>
                <span style={{ display: 'none' }} data-testid={`secondary-subtitle-${index}`}>
                  <Trans message={feature.subtitle} />
                </span>
                <span style={{ display: 'none' }} data-testid={`secondary-image-${index}`}>{feature.image}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── FAQ ── */
const FAQ_ITEMS = [
  { q: 'What is a link in bio page?', a: 'A link in bio page is a single URL that holds all your important links in one place. Instead of updating your Instagram or TikTok bio every time you post, your link stays the same — and you update what\'s behind it. It works on every platform that restricts you to one clickable bio link.' },
  { q: 'What is a URL shortener and how does it work?', a: 'A URL shortener converts a long web address into a short, clean link that is easier to share. Short links are useful in social posts, emails, and printed materials. With LinkInBio, every shortened link also includes click tracking so you can see how each one performs.' },
  { q: 'Can I use my own domain name?', a: 'No. Custom domains are not currently supported. All short links and bio pages are generated using our platform domains, making it easy to create and share links without any additional setup or configuration.' },
  { q: 'Which platforms does LinkInBio work with?', a: 'LinkInBio works with any platform that allows a URL in the bio including Instagram, TikTok, Twitter/X, YouTube, LinkedIn, Pinterest, and Snapchat. You add your unique link once and point your audience there from every channel.' },
  { q: 'What analytics data does LinkInBio track?', a: 'Every link is tracked automatically. The dashboard shows total clicks, unique visitors, traffic sources, geographic location, device type, browser, and more. You can view analytics for individual links or combined across a link group.' },
  { q: 'What is a retargeting pixel and how does it work here?', a: 'A retargeting pixel is a small code snippet from an ad platform such as Meta, Google, or TikTok that tracks visitors to your links. When someone clicks a link with a pixel attached, they are added to your retargeting audience. You can then show them relevant ads on those platforms later. LinkInBio handles the pixel attachment without any coding.' },
  { q: 'What does password protection do?', a: 'Password protection restricts a link so only people with the correct password can reach the destination. This is useful for sharing exclusive content with paid members, distributing private documents, or limiting access to invite-only pages.' },
  { q: 'What are link groups?', a: 'Link groups let you organise related links under one label — for example, all links for a campaign, a client, or a specific content type. Groups make it easier to manage large numbers of links and let you view combined analytics across the entire group at once.' },
  { q: 'Is LinkInBio free to use?', a: 'Yes. LinkInBio has a free plan that includes link shortening, a bio link page, and basic analytics — no credit card required. Paid plans unlock higher usage limits, advanced analytics, retargeting pixels, and custom domains. You can upgrade any time from your dashboard.' },
];

function FaqSection() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section id="faq" className="lp-faq-section">
      <div className="lp-faq-inner">

        {/* Left: heading */}
        <div className="lp-faq-left">
          <small className="section-label">FAQ</small>
          <h2 className="lf-heading" style={{ marginTop: 8 }}>
            Got questions?<br />We&apos;ve got answers.
          </h2>
          <p className="lp-faq-desc">
            Everything you need to know about LinkinBio. Can&apos;t find what you&apos;re looking for?{' '}
            <a href="/contact" className="lp-faq-link">Reach out to us.</a>
          </p>
        </div>

        {/* Right: accordion */}
        <div className="lp-faq-list">
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                className={`lp-faq-item ${isOpen ? 'lp-faq-open' : ''}`}
                style={{ animationDelay: `${0.04 + i * 0.04}s` }}
              >
                <button
                  className="lp-faq-trigger"
                  onClick={() => setOpen(isOpen ? null : i)}
                >
                  <span className="lp-faq-q">{item.q}</span>
                  <span className="lp-faq-icon">
                    <svg
                      width="16" height="16" viewBox="0 0 16 16" fill="none"
                      style={{ transform: isOpen ? 'rotate(45deg)' : 'rotate(0)', transition: 'transform 0.25s ease' }}
                    >
                      <line x1="8" y1="2" x2="8" y2="14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      <line x1="2" y1="8" x2="14" y2="8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  </span>
                </button>
                <div className="lp-faq-body" style={{ maxHeight: isOpen ? 400 : 0 }}>
                  <p className="lp-faq-a">{item.a}</p>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}

/* ── INTERNAL LINKS ── */
function InternalLinksSection() {
  return (
    <div className="landing-container py-40 text-center">
      <p className="text-sm text-muted max-w-[800px] mx-auto">
        Ready to create your own{' '}
        <Link to="/for" className="text-primary font-medium">
          Link in Bio
        </Link>
        ? Browse our{' '}
        <Link to="/use-cases" className="text-primary font-medium">
          link in bio website
        </Link>{' '}
        guides for real examples, explore every{' '}
        <Link to="/features" className="text-primary font-medium">
          link in bio tool
        </Link>{' '}
        we offer, or just get started, since LinkInBio is currently{' '}
        <Link to="/platforms" className="text-primary font-medium">
          Link in bio free
        </Link>{' '}
        for our first 500 signups.
      </p>
    </div>
  );
}

/* ── GET STARTED CTA ── */
function GetStartedCta({ content }: ContentProps) {
  const { hasPermission } = useAuth();
  const { links: { homepage_creation } } = useSettings();

  return (
    <div className="lp-cta-section">
      <div className="landing-container" style={{ textAlign: 'center' }}>
        <h2 className="lp-cta-heading">
          {content.footerTitle || 'Your link is waiting.'}
        </h2>
        <p className="lp-cta-sub">
          Join 120,000+ creators who turned their bio into a home. It takes three minutes and it&apos;s free.
        </p>
        {homepage_creation && hasPermission('links.create') && (
          <div className="mt-40 max-w-xl mx-auto">
            <LandingPageNewLinkForm />
          </div>
        )}
      </div>
    </div>
  );
}
